import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LineChart, Line, Cell, PieChart, Pie,
} from 'recharts';
import {
  HiOutlineArrowPath,
  HiOutlineChartBar,
  HiOutlineTableCells,
} from 'react-icons/hi2';
import { authService, machineService, oeeService, masterDataService, plantService, reportService } from '../../services';
import apiClient from '../../config/api';
import './PlantDashboard.css';
import { CHART_PALETTE, SEMANTIC_SUCCESS_MID } from '../../theme/chartColors';

const oeeColor = (v) => (v >= 85 ? SEMANTIC_SUCCESS_MID : v >= 60 ? '#eab308' : '#dc2626');
const COLORS = CHART_PALETTE;

const PlantDashboard = () => {
  /* ──── state ──── */
  const [machines, setMachines] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [plants, setPlants] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [selectedShift, setSelectedShift] = useState('');
  const [selectedPlant, setSelectedPlant] = useState('');
  const [viewMode, setViewMode] = useState('chart'); // 'chart' | 'table'
  const [machineStats, setMachineStats] = useState({});
  const [trendData, setTrendData] = useState([]);
  const [downtimeReport, setDowntimeReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const tenantId = authService.getTenantId();

  useEffect(() => { loadInitialData(); }, []);

  useEffect(() => {
    if (selectedShift && machines.length > 0) {
      loadDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedShift, selectedPlant, machines]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [shiftsData, machinesData, plantsData] = await Promise.all([
        masterDataService.getShifts(tenantId),
        machineService.getAllMachines(),
        plantService.getAllPlants(),
      ]);
      setShifts(shiftsData || []);
      setMachines(machinesData || []);
      setPlants(plantsData || []);

      // Auto-select plant if only 1 exists
      if (plantsData?.length === 1) {
          const pId = plantsData[0].plantId.toString();
          setSelectedPlant(pId);
      }
      
      if (shiftsData?.length > 0) setSelectedShift(shiftsData[0].shiftId);
    } catch (err) {
      console.error('Failed to load initial data', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePlantChange = (val) => {
    setSelectedPlant(val);
    if (val) localStorage.setItem('defaultPlantId', val);
  };

  const loadDashboardData = async () => {
    setRefreshing(true);
    const filteredMachines = selectedPlant
      ? machines.filter(m => m.plantId === parseInt(selectedPlant))
      : machines;

    /* 1. Per-machine shift metrics */
    const stats = {};
    await Promise.all(
      filteredMachines.map(async (machine) => {
        try {
          const [activeRun, metrics] = await Promise.all([
            oeeService.getActiveRun(machine.machineId),
            oeeService.getShiftMetrics(machine.machineId, selectedShift, selectedDate).catch(() => null),
          ]);
          stats[machine.machineId] = {
            machineName: machine.machineName,
            machineCode: machine.machineCode,
            plantId: machine.plantId,
            oee: metrics?.oee ?? 0,
            availability: metrics?.availability ?? 0,
            performance: metrics?.performance ?? 0,
            quality: metrics?.quality ?? 0,
            status: activeRun ? 'Running' : 'Stopped',
            totalProduced: metrics?.totalProduced ?? 0,
            totalRejects: metrics?.totalRejects ?? 0,
            totalRework: metrics?.totalRework ?? 0,
            totalPlannedDowntimeMinutes: metrics?.totalPlannedDowntimeMinutes ?? 0,
            totalUnplannedDowntimeMinutes: metrics?.totalUnplannedDowntimeMinutes ?? 0,
            shiftDurationMinutes: metrics?.shiftDurationMinutes ?? 0,
          };
        } catch {
          stats[machine.machineId] = {
            machineName: machine.machineName, machineCode: machine.machineCode,
            status: 'Stopped', oee: 0, availability: 0, performance: 0, quality: 0,
            totalProduced: 0, totalRejects: 0, totalRework: 0,
            totalPlannedDowntimeMinutes: 0, totalUnplannedDowntimeMinutes: 0, shiftDurationMinutes: 0,
          };
        }
      }),
    );
    setMachineStats(stats);

    /* 2. 7-day OEE trend */
    try {
      const trendDays = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date(selectedDate);
        d.setDate(d.getDate() - i);
        trendDays.push(d.toISOString().slice(0, 10));
      }
      const trendResults = await Promise.all(
        trendDays.map(async (day) => {
          let dayOee = 0; let count = 0;
          await Promise.all(
            filteredMachines.slice(0, 10).map(async (m) => {
              try {
                const met = await oeeService.getShiftMetrics(m.machineId, selectedShift, day);
                if (met?.oee != null) { dayOee += met.oee; count++; }
              } catch { /* ignore */ }
            }),
          );
          return { date: day.slice(5), oee: count > 0 ? +(dayOee / count).toFixed(1) : 0 };
        }),
      );
      setTrendData(trendResults);
    } catch { setTrendData([]); }

    /* 3. Downtime report */
    try {
      const dt = await reportService.getDowntimeReport(
        new Date(selectedDate), new Date(selectedDate + 'T23:59:59')
      );
      setDowntimeReport(dt || []);
    } catch { setDowntimeReport([]); }

    setRefreshing(false);
  };

  /* ──── derived data ──── */
  const statsArray = useMemo(() => Object.values(machineStats), [machineStats]);
  const machinesRunning = useMemo(() => statsArray.filter(s => s.status === 'Running').length, [statsArray]);
  const machinesStopped = useMemo(() => statsArray.filter(s => s.status !== 'Running').length, [statsArray]);
  const avgOee = useMemo(() => {
    if (statsArray.length === 0) return 0;
    return +(statsArray.reduce((a, s) => a + (s.oee || 0), 0) / statsArray.length).toFixed(1);
  }, [statsArray]);
  const avgAvailability = useMemo(() => {
    if (statsArray.length === 0) return 0;
    return +(statsArray.reduce((a, s) => a + (s.availability || 0), 0) / statsArray.length).toFixed(1);
  }, [statsArray]);
  const avgPerformance = useMemo(() => {
    if (statsArray.length === 0) return 0;
    return +(statsArray.reduce((a, s) => a + (s.performance || 0), 0) / statsArray.length).toFixed(1);
  }, [statsArray]);
  const avgQuality = useMemo(() => {
    if (statsArray.length === 0) return 0;
    return +(statsArray.reduce((a, s) => a + (s.quality || 0), 0) / statsArray.length).toFixed(1);
  }, [statsArray]);
  const totalProduction = useMemo(() => statsArray.reduce((a, s) => a + (s.totalProduced || 0), 0), [statsArray]);
  const totalDowntime = useMemo(() =>
    statsArray.reduce((a, s) => a + (s.totalPlannedDowntimeMinutes || 0) + (s.totalUnplannedDowntimeMinutes || 0), 0).toFixed(1),
    [statsArray]);

  const machineOeeData = useMemo(() =>
    statsArray.map(s => ({ name: s.machineName || s.machineCode, oee: +s.oee.toFixed(1) })).sort((a, b) => b.oee - a.oee),
    [statsArray]);

  const apqData = useMemo(() =>
    statsArray.map(s => ({
      name: s.machineName || s.machineCode,
      Availability: +s.availability.toFixed(1),
      Performance: +s.performance.toFixed(1),
      Quality: +s.quality.toFixed(1),
    })),
    [statsArray]);

  const downtimePareto = useMemo(() => {
    const map = {};
    statsArray.forEach(s => {
      if (s.totalPlannedDowntimeMinutes > 0) {
        const key = `${s.machineName} - Planned`;
        map[key] = (map[key] || 0) + s.totalPlannedDowntimeMinutes;
      }
      if (s.totalUnplannedDowntimeMinutes > 0) {
        const key = `${s.machineName} - Unplanned`;
        map[key] = (map[key] || 0) + s.totalUnplannedDowntimeMinutes;
      }
    });
    const sorted = Object.entries(map).sort((a, b) => b[1] - a[1]).map(([name, value]) => ({ name, value: +value.toFixed(1) }));
    let cum = 0; const total = sorted.reduce((a, s) => a + s.value, 0);
    return sorted.map(s => { cum += s.value; return { ...s, cumPercent: total > 0 ? +((cum / total) * 100).toFixed(1) : 0 }; });
  }, [statsArray]);

  const productionData = useMemo(() =>
    statsArray.map(s => ({
      name: s.machineName || s.machineCode,
      produced: s.totalProduced || 0,
      rejects: s.totalRejects || 0,
      rework: s.totalRework || 0,
    })),
    [statsArray]);

  const machineStatusGrid = useMemo(() =>
    statsArray.map(s => ({ name: s.machineName || s.machineCode, status: s.status, oee: s.oee })),
    [statsArray]);

  if (loading) {
    return (<div className="pd-loading"><div className="pd-spinner" /><p>Loading Plant Dashboard...</p></div>);
  }

  return (
    <div className="plant-dashboard">
      {/* ── Header / Filters ── */}
      <div className="pd-header">
        <div className="pd-header-left">
          <h1 className="pd-title">Plant Dashboard</h1>
          <span className="pd-subtitle">Real-time OEE Performance Overview</span>
        </div>
        <div className="pd-filters">
          <input type="date" className="pd-filter-input" value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)} />
          <select className="pd-filter-select" value={selectedShift}
            onChange={e => setSelectedShift(e.target.value)}>
            {shifts.map(s => (
              <option key={s.shiftId} value={s.shiftId}>
                {s.shiftCode} ({s.startTime?.slice(0, 5)} – {s.endTime?.slice(0, 5)})
              </option>
            ))}
          </select>
          {plants.length > 0 && (
            <select className="pd-filter-select" value={selectedPlant}
              onChange={e => handlePlantChange(e.target.value)}>
              <option value="">All Plants</option>
              {plants.map(p => (
                <option key={p.plantId} value={p.plantId}>{p.plantName || p.plantCode}</option>
              ))}
            </select>
          )}
          {/* View Toggle */}
          <div className="pd-view-toggle">
            <button className={`pd-view-btn ${viewMode === 'chart' ? 'pd-view-active' : ''}`}
              onClick={() => setViewMode('chart')}>
              <HiOutlineChartBar /> Charts
            </button>
            <button className={`pd-view-btn ${viewMode === 'table' ? 'pd-view-active' : ''}`}
              onClick={() => setViewMode('table')}>
              <HiOutlineTableCells /> Tables
            </button>
          </div>
          <button className="pd-refresh-btn" onClick={loadDashboardData} disabled={refreshing}>
            <HiOutlineArrowPath className={refreshing ? 'pd-spin' : ''} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div className="pd-kpi-row">
        <KpiCard label="Overall OEE" value={`${avgOee}%`} color={oeeColor(avgOee)} icon="📊" large />
        <KpiCard label="Availability" value={`${avgAvailability}%`} color="#0ea5e9" icon="🔵" />
        <KpiCard label="Performance" value={`${avgPerformance}%`} color={SEMANTIC_SUCCESS_MID} icon="🟢" />
        <KpiCard label="Quality" value={`${avgQuality}%`} color="#8b5cf6" icon="🟣" />
        <KpiCard label="Running" value={machinesRunning} color="#16a34a" icon="▶️" />
        <KpiCard label="Stopped" value={machinesStopped} color="#dc2626" icon="⏹️" />
        <KpiCard label="Total Production" value={totalProduction.toLocaleString()} color="#f59e0b" icon="🏭" />
        <KpiCard label="Total Downtime" value={`${totalDowntime} min`} color="#ef4444" icon="⏱️" />
      </div>

      {/* ═══════════════ CHART VIEW ═══════════════ */}
      {viewMode === 'chart' && (
        <>
          <div className="pd-charts-row">
            <div className="pd-chart-card pd-chart-half">
              <h3 className="pd-chart-title">Machine-wise OEE</h3>
              {machineOeeData.length === 0 ? <div className="pd-empty">No machine data</div> : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={machineOeeData} layout="vertical" margin={{ left: 20, right: 20, top: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" domain={[0, 100]} tickFormatter={v => `${v}%`} />
                    <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={v => `${v}%`} />
                    <Bar dataKey="oee" radius={[0, 6, 6, 0]} barSize={22}>
                      {machineOeeData.map((entry, i) => (<Cell key={i} fill={oeeColor(entry.oee)} />))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="pd-chart-card pd-chart-half">
              <h3 className="pd-chart-title">Availability vs Performance vs Quality</h3>
              {apqData.length === 0 ? <div className="pd-empty">No data</div> : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={apqData} margin={{ left: 20, right: 20, top: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} />
                    <Tooltip formatter={v => `${v}%`} /><Legend />
                    <Bar dataKey="Availability" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Performance" fill={SEMANTIC_SUCCESS_MID} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Quality" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="pd-charts-row">
            <div className="pd-chart-card pd-chart-half">
              <h3 className="pd-chart-title">Downtime Pareto</h3>
              {downtimePareto.length === 0 ? <div className="pd-empty">No downtime data</div> : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={downtimePareto} margin={{ left: 20, right: 40, top: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis yAxisId="left" tickFormatter={v => `${v} min`} />
                    <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tickFormatter={v => `${v}%`} />
                    <Tooltip />
                    <Bar yAxisId="left" dataKey="value" radius={[4, 4, 0, 0]} barSize={28}>
                      {downtimePareto.map((_, i) => (<Cell key={i} fill={COLORS[i % COLORS.length]} />))}
                    </Bar>
                    <Line yAxisId="right" type="monotone" dataKey="cumPercent" stroke="#6366f1" strokeWidth={2} dot={{ r: 4 }} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="pd-chart-card pd-chart-half">
              <h3 className="pd-chart-title">OEE Trend (7 Days)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trendData} margin={{ left: 20, right: 20, top: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} />
                  <Tooltip formatter={v => `${v}%`} />
                  <Line type="monotone" dataKey="oee" stroke="#6366f1" strokeWidth={3} dot={{ r: 5, fill: '#6366f1' }} activeDot={{ r: 7 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="pd-charts-row">
            <div className="pd-chart-card pd-chart-half">
              <h3 className="pd-chart-title">Production Summary by Machine</h3>
              {productionData.length === 0 ? <div className="pd-empty">No production data</div> : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={productionData} margin={{ left: 20, right: 20, top: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis /><Tooltip /><Legend />
                    <Bar dataKey="produced" name="Good Units" fill="#16a34a" stackId="a" />
                    <Bar dataKey="rejects" name="Rejects" fill="#ef4444" stackId="a" />
                    <Bar dataKey="rework" name="Rework" fill="#f59e0b" stackId="a" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="pd-chart-card pd-chart-half">
              <h3 className="pd-chart-title">Live Machine Status</h3>
              <div className="pd-machine-grid">
                {machineStatusGrid.map((m, i) => (
                  <div key={i} className={`pd-machine-tile ${m.status === 'Running' ? 'pd-tile-running' : 'pd-tile-stopped'}`}>
                    <span className="pd-tile-name">{m.name}</span>
                    <span className="pd-tile-oee">{m.oee?.toFixed(1)}%</span>
                    <span className={`pd-tile-status ${m.status === 'Running' ? 'running' : 'stopped'}`}>{m.status}</span>
                  </div>
                ))}
                {machineStatusGrid.length === 0 && <div className="pd-empty">No machines found</div>}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ═══════════════ TABLE VIEW ═══════════════ */}
      {viewMode === 'table' && (
        <>
          {/* Shift Summary Table */}
          <div className="pd-chart-card pd-table-full">
            <h3 className="pd-chart-title">Shift Summary</h3>
            <div className="pd-table-wrapper">
              <table className="pd-table">
                <thead>
                  <tr>
                    <th>Machine</th><th>Status</th><th>OEE %</th><th>Availability %</th>
                    <th>Performance %</th><th>Quality %</th><th>Produced</th><th>Rejects</th>
                    <th>Rework</th><th>Planned DT</th><th>Unplanned DT</th>
                  </tr>
                </thead>
                <tbody>
                  {statsArray.length === 0 ? (
                    <tr><td colSpan="11" className="pd-table-empty">No data available</td></tr>
                  ) : (
                    statsArray.map((s, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'pd-row-even' : ''}>
                        <td className="pd-cell-name">{s.machineName || s.machineCode}</td>
                        <td>
                          <span className={`pd-status-badge ${s.status === 'Running' ? 'pd-badge-running' : 'pd-badge-stopped'}`}>
                            {s.status}
                          </span>
                        </td>
                        <td style={{ color: oeeColor(s.oee), fontWeight: 700 }}>{s.oee?.toFixed(1)}%</td>
                        <td>{s.availability?.toFixed(1)}%</td>
                        <td>{s.performance?.toFixed(1)}%</td>
                        <td>{s.quality?.toFixed(1)}%</td>
                        <td>{s.totalProduced}</td>
                        <td className="pd-cell-reject">{s.totalRejects}</td>
                        <td className="pd-cell-rework">{s.totalRework}</td>
                        <td>{s.totalPlannedDowntimeMinutes?.toFixed(1)} min</td>
                        <td>{s.totalUnplannedDowntimeMinutes?.toFixed(1)} min</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Downtime Report Table */}
          <div className="pd-chart-card pd-table-full">
            <h3 className="pd-chart-title">Downtime Report</h3>
            <div className="pd-table-wrapper">
              <table className="pd-table">
                <thead>
                  <tr>
                    <th>Date</th><th>Machine</th><th>Machine Code</th><th>Plant</th>
                    <th>Line</th><th>Shift</th><th>Planned DT (min)</th>
                    <th>Unplanned DT (min)</th><th>Total DT (min)</th>
                  </tr>
                </thead>
                <tbody>
                  {downtimeReport.length === 0 ? (
                    <tr><td colSpan="9" className="pd-table-empty">No downtime data</td></tr>
                  ) : (
                    downtimeReport.map((r, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'pd-row-even' : ''}>
                        <td>{r['Date']}</td>
                        <td className="pd-cell-name">{r['Machine']}</td>
                        <td>{r['Machine Code']}</td>
                        <td>{r['Plant']}</td>
                        <td>{r['Line']}</td>
                        <td>{r['Shift']}</td>
                        <td>{r['Planned Downtime (min)']}</td>
                        <td className="pd-cell-reject">{r['Unplanned Downtime (min)']}</td>
                        <td style={{ fontWeight: 600 }}>{r['Total Downtime (min)']}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Production Summary Table */}
          <div className="pd-chart-card pd-table-full">
            <h3 className="pd-chart-title">Production Summary</h3>
            <div className="pd-table-wrapper">
              <table className="pd-table">
                <thead>
                  <tr>
                    <th>Machine</th><th>Good Units</th><th>Rejects</th><th>Rework</th>
                    <th>Total Units</th><th>Quality %</th>
                  </tr>
                </thead>
                <tbody>
                  {statsArray.length === 0 ? (
                    <tr><td colSpan="6" className="pd-table-empty">No production data</td></tr>
                  ) : (
                    statsArray.map((s, i) => {
                      const total = (s.totalProduced || 0) + (s.totalRejects || 0) + (s.totalRework || 0);
                      return (
                        <tr key={i} className={i % 2 === 0 ? 'pd-row-even' : ''}>
                          <td className="pd-cell-name">{s.machineName || s.machineCode}</td>
                          <td>{s.totalProduced}</td>
                          <td className="pd-cell-reject">{s.totalRejects}</td>
                          <td className="pd-cell-rework">{s.totalRework}</td>
                          <td style={{ fontWeight: 600 }}>{total}</td>
                          <td style={{ color: oeeColor(s.quality), fontWeight: 700 }}>{s.quality?.toFixed(1)}%</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const KpiCard = ({ label, value, color, icon, large }) => (
  <div className={`pd-kpi-card ${large ? 'pd-kpi-large' : ''}`}>
    <div className="pd-kpi-icon" style={{ background: `${color}18` }}><span>{icon}</span></div>
    <div className="pd-kpi-info">
      <span className="pd-kpi-value" style={{ color }}>{value}</span>
      <span className="pd-kpi-label">{label}</span>
    </div>
  </div>
);

export default PlantDashboard;
